/* ==========================================================================
   CF Editorial — Scroll reveal, parallax, stagger & cursor controller
   Casey Fuchs Jewelry · caseyfuchs.com
   
   Works with cf-editorial.css. Applied at runtime — no Liquid changes needed
   beyond the script include in theme.liquid.
   ========================================================================== */

(function () {
  'use strict';

  // Bail out entirely if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  if (prefersReducedMotion) return;

  /* -----------------------------------------------------------------------
     1. Scroll Reveal — IntersectionObserver
     ----------------------------------------------------------------------- */

  function initRevealObserver() {
    const revealElements = document.querySelectorAll('.cf-reveal, .cf-reveal--fade, .cf-reveal--clip, .cf-reveal--scale, .cf-scroll-zoom');

    if (!revealElements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    revealElements.forEach((el) => observer.observe(el));
  }

  /* -----------------------------------------------------------------------
     2. Auto-apply reveal classes to theme elements
     ----------------------------------------------------------------------- */

  function applyRevealClasses() {
    // Section headers — subtle fade up
    document.querySelectorAll('.text-heading-standard').forEach((el) => {
      // Don't apply to elements inside the header/nav
      if (el.closest('header') || el.closest('nav')) return;
      el.classList.add('cf-reveal');
    });

    // Rich text sections — body text
    document.querySelectorAll('section .px-section-horizontal-spacing p, section .px-section-horizontal-spacing .rte').forEach((el) => {
      if (el.closest('header') || el.closest('nav') || el.closest('footer')) return;
      if (el.closest('.cf-reveal')) return; // avoid double-applying
      el.classList.add('cf-reveal--fade');
    });

    // Featured collection grids — stagger children
    document.querySelectorAll('.featured-collection ul.grid').forEach((grid) => {
      const items = grid.querySelectorAll(':scope > li:not([aria-hidden])');
      items.forEach((item, index) => {
        item.classList.add('cf-reveal');
        item.setAttribute('data-cf-delay', Math.min(index + 1, 12));
      });
    });

    // Product list sections — stagger
    document.querySelectorAll('section .product-list-item, section [class*="product-list"] li').forEach((item, index) => {
      if (item.getAttribute('aria-hidden') === 'true') return;
      item.classList.add('cf-reveal');
      item.setAttribute('data-cf-delay', Math.min((index % 6) + 1, 12));
    });

    // Main collection page grids
    document.querySelectorAll('.main-collection ul.grid > li, [class*="collection-grid"] > li').forEach((item, index) => {
      if (item.getAttribute('aria-hidden') === 'true') return;
      item.classList.add('cf-reveal', 'cf-scroll-zoom');
      item.setAttribute('data-cf-delay', Math.min((index % 6) + 1, 12));
    });

    // Slideshow — skip reveal animation (hero is already visible on load)

    // Image-with-text, media-split, and similar editorial sections
    document.querySelectorAll(
      '[class*="image-with-text"] > div, [class*="media-split"] > div, [class*="text-split"] > div'
    ).forEach((el) => {
      el.classList.add('cf-reveal--fade');
    });

    // Video sections
    document.querySelectorAll('[class*="video-with-text-overlay"], [class*="video.liquid"]').forEach((el) => {
      el.classList.add('cf-reveal--scale');
    });

    // Scrolling text (marquee)
    document.querySelectorAll('[class*="scrolling-text"], [class*="scrolling-items"]').forEach((el) => {
      const section = el.closest('section');
      if (section) {
        section.classList.add('cf-reveal--fade');
      }
    });

    // Product tiles on all pages — scroll zoom
    document.querySelectorAll('data-island.group\\/zoom').forEach((tile) => {
      const imageContainer = tile.querySelector('.relative');
      if (imageContainer && !imageContainer.classList.contains('cf-scroll-zoom')) {
        imageContainer.classList.add('cf-scroll-zoom');
      }
    });

    // Footer — gentle fade
    document.querySelectorAll('footer, [class*="footer"]').forEach((el) => {
      if (el.tagName === 'FOOTER' || el.closest('footer')) {
        el.classList.add('cf-reveal--fade');
      }
    });
  }

  /* -----------------------------------------------------------------------
     3. Parallax on Hero / Banner images
     ----------------------------------------------------------------------- */

  function initParallax() {
    const parallaxElements = document.querySelectorAll(
      '.splide__slide img, [class*="image-with-text-overlay"] img'
    );

    if (!parallaxElements.length) return;

    // Wrap images for parallax transform
    parallaxElements.forEach((img) => {
      if (img.closest('.cf-parallax')) return;
      const wrapper = img.parentElement;
      if (wrapper) {
        wrapper.style.overflow = 'hidden';
        img.classList.add('cf-parallax');
      }
    });

    let ticking = false;

    function updateParallax() {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;

      document.querySelectorAll('.cf-parallax').forEach((el) => {
        const rect = el.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const viewportCenter = viewportHeight / 2;
        const distance = elementCenter - viewportCenter;
        const intensity = parseFloat(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--cf-parallax-intensity')
        ) || 0.06;

        const translateY = distance * intensity;
        el.style.transform = `translateY(${translateY}px) scale(1.05)`;
      });

      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });

    // Initial position
    updateParallax();
  }

  /* -----------------------------------------------------------------------
     4. Custom Cursor — Minimal Dot
     ----------------------------------------------------------------------- */

  function initCursor() {
    // Don't init on touch devices
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

    const cursor = document.createElement('div');
    cursor.classList.add('cf-cursor');
    document.body.appendChild(cursor);

    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      if (!cursor.classList.contains('is-active')) {
        cursor.classList.add('is-active');
      }
    });

    document.addEventListener('mouseleave', () => {
      cursor.classList.remove('is-active');
    });

    document.addEventListener('mouseenter', () => {
      cursor.classList.add('is-active');
    });

    // Smooth cursor follow with lerp
    function animateCursor() {
      const dx = mouseX - cursorX;
      const dy = mouseY - cursorY;

      // Lerp factor — lower = smoother/slower
      cursorX += dx * 0.15;
      cursorY += dy * 0.15;

      cursor.style.left = cursorX + 'px';
      cursor.style.top = cursorY + 'px';

      requestAnimationFrame(animateCursor);
    }

    animateCursor();

    // Hover state for interactive elements
    const hoverTargets = 'a, button, input, select, textarea, [role="button"], .splide__arrow, .splide__pagination__page, data-island.group\\/zoom';

    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(hoverTargets)) {
        cursor.classList.add('is-hovering');
      }
    });

    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(hoverTargets)) {
        cursor.classList.remove('is-hovering');
      }
    });
  }

  /* -----------------------------------------------------------------------
     5. Init Everything
     ----------------------------------------------------------------------- */

  function init() {
    applyRevealClasses();
    initRevealObserver();
    initParallax();
    initCursor();
  }

  // Run on DOMContentLoaded if not fired yet, otherwise immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-apply on Shopify section rendering (theme editor support)
  document.addEventListener('shopify:section:load', () => {
    applyRevealClasses();
    initRevealObserver();
  });
})();
